import { CommonPage, Content, LoadableContent, PageTitle, ResponsiveGap } from "common/Grid";

import { TableView } from "./TableView";
import { useGetInvoiceListQuery } from "store/action/invoiceApi";
import { PrimaryButton } from "common/Forms";
import { useNavigate, createSearchParams, useSearchParams } from "react-router-dom";
import { WithPagination } from "common/Pagination";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { IfHasPermission } from "context/PermissionsContext";
import { PermissionKeys } from "Constant/Constant";

export const Page = () => {

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const searchFields = useMemo(() => ({
    page: { name: 'p', default: 1 },
    limit: { name: 'ps', default: 10 },
  }), []);

  const getSearchValue = useCallback((name, defaultValue, getter = (value) => value) => {
    const value = searchParams.get(name);
    if (value === null) {
      return defaultValue;
    }
    return getter(value);
  }, [searchParams]);

  const page = useRef(getSearchValue(searchFields.page.name, searchFields.page.default, x => parseInt(x) ?? searchFields.page.default));
  const limit = useRef(getSearchValue(searchFields.limit.name, searchFields.limit.default, x => parseInt(x) ?? searchFields.limit.default));

  const applySearch = useCallback(() => {
    const updatedSearchParams = createSearchParams(searchParams);
    updatedSearchParams.set(searchFields.page.name, `${page.current}`);
    updatedSearchParams.set(searchFields.limit.name, `${limit.current}`);
    setSearchParams(updatedSearchParams);
  }, [searchFields, searchParams, setSearchParams]);

  const onPageHandler = useCallback((pageRequest) => {
    page.current = pageRequest + 1;
    applySearch();
  }, [applySearch]);

  const onPageSizeHandler = useCallback((sizeRequest) => {
    limit.current = sizeRequest;
    page.current = 1;
    applySearch();
  }, [applySearch]);

  useEffect(() => {
    applySearch();
  }, [applySearch]);

  const { data: invoiceList, isLoading, isError } = useGetInvoiceListQuery(
    {
      page: getSearchValue(searchFields.page.name, searchFields.page.default, x => parseInt(x)),
      numOfRows: getSearchValue(searchFields.limit.name, searchFields.limit.default, x => parseInt(x)),
    },
    { refetchOnMountOrArgChange: true }
  );

  return (
    <>
      <CommonPage>
        <Content>
          <PageTitle title="Invoice" />
          <ResponsiveGap right={false} left={false} classNames={["w-full flex items-center justify-end"]}>
              <IfHasPermission permissionKey={PermissionKeys.GENERATE_INVOICE}>
                <PrimaryButton action={() => navigate("/generate-invoice")} classNames={["min-w-[180px]"]}>
                  Generate Invoice
                </PrimaryButton>
              </IfHasPermission>
          </ResponsiveGap>
          <LoadableContent isLoading={isLoading} isError={isError}>
            <WithPagination id='pagedData'
              currentPage={(invoiceList?.currentPage ?? 1) - 1}
              totalPages={invoiceList?.totalPages ?? 0}
              totalItems={invoiceList?.total ?? 0}
              pageSize={limit.current ?? 0}
              onPageChange={onPageHandler}
              onPageSizeChange={onPageSizeHandler}
              paginationWrapper={x => <ResponsiveGap>{x}</ResponsiveGap>}>
              <TableView invoiceList={invoiceList?.data} />
            </WithPagination>
          </LoadableContent>
        </Content>
      </CommonPage>
    </>
  );
};
